import { useEffect, useState } from 'react';
import { api } from '@/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Label } from 'recharts';
import { format, subDays, subMonths } from 'date-fns';

interface HoursData {
  groupBy: string;
  data: Array<{
    id?: string;
    name?: string;
    date?: string;
    color?: string;
    totalHours: number;
  }>;
}

type TimeRange = '7days' | '30days' | '90days';
type GroupBy = 'project' | 'user' | 'day';

export default function AdminReportsPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30days');
  const [groupBy, setGroupBy] = useState<GroupBy>('project');
  const [data, setData] = useState<HoursData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [timeRange, groupBy]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      let startDate: string;

      switch (timeRange) {
        case '7days':
          startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
          break;
        case '30days':
          startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
          break;
        case '90days':
          startDate = format(subMonths(new Date(), 3), 'yyyy-MM-dd');
          break;
      }

      const result = await api.get<HoursData>('/admin/hours', {
        groupBy,
        startDate,
        endDate,
      });
      setData(result);
    } catch (err) {
      console.error('Failed to load report data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data) return;

    let csv = '';
    if (groupBy === 'project') {
      csv = 'Project,Hours\n';
      data.data.forEach((item) => {
        csv += `"${item.name}",${item.totalHours}\n`;
      });
    } else if (groupBy === 'user') {
      csv = 'User,Hours\n';
      data.data.forEach((item) => {
        csv += `"${item.name}",${item.totalHours}\n`;
      });
    } else {
      csv = 'Date,Hours\n';
      data.data.forEach((item) => {
        csv += `${item.date},${item.totalHours}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hours-report-${groupBy}-${timeRange}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalHours = data?.data.reduce((sum, item) => sum + item.totalHours, 0) || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Reports</h1>
          <p className="text-dark-400 mt-1">Analyze hours and team performance</p>
        </div>
        <button onClick={exportCSV} className="btn-secondary" disabled={!data}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="label">Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="input w-auto"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
          </div>
          <div>
            <label className="label">Group By</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="input w-auto"
            >
              <option value="project">Project</option>
              <option value="user">User</option>
              <option value="day">Day</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Total Hours</p>
          <p className="text-3xl font-bold text-white mt-1">{totalHours}h</p>
        </div>
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Average per Day</p>
          <p className="text-3xl font-bold text-primary-400 mt-1">
            {groupBy === 'day' && data?.data.length ? Math.round(totalHours / data.data.length) : '--'}h
          </p>
        </div>
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Data Points</p>
          <p className="text-3xl font-bold text-accent-400 mt-1">{data?.data.length || 0}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">
          Hours by {groupBy === 'project' ? 'Project' : groupBy === 'user' ? 'User' : 'Day'}
        </h2>

        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="spinner w-8 h-8" />
          </div>
        ) : data && data.data.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            {groupBy === 'day' ? (
              <LineChart data={data.data} margin={{ left: 10, right: 10, top: 5, bottom: 25 }}>
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                >
                  <Label value="Date" position="bottom" offset={5} fill="#94a3b8" fontSize={12} />
                </XAxis>
                <YAxis stroke="#64748b">
                  <Label value="Hours" angle={-90} position="insideLeft" offset={10} fill="#94a3b8" fontSize={12} />
                </YAxis>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                  labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                />
                <Line
                  type="monotone"
                  dataKey="totalHours"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 0 }}
                />
              </LineChart>
            ) : (
              <BarChart data={data.data} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 25 }}>
                <XAxis type="number" stroke="#64748b" fontSize={12}>
                  <Label value="Hours" position="bottom" offset={5} fill="#94a3b8" fontSize={12} />
                </XAxis>
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#64748b"
                  width={150}
                  tick={{ fontSize: 12, fill: '#94a3b8' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                />
                <Bar
                  dataKey="totalHours"
                  fill="#3b82f6"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-80 flex items-center justify-center text-dark-500">
            No data available for the selected period
          </div>
        )}
      </div>

      {/* Data table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Detailed Data</h2>
        {data && data.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{groupBy === 'day' ? 'Date' : 'Name'}</th>
                  <th className="text-right">Hours</th>
                  <th className="text-right">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((item, index) => (
                  <tr key={index}>
                    <td className="text-dark-200">
                      {groupBy === 'day'
                        ? format(new Date(item.date!), 'MMM d, yyyy')
                        : item.name}
                    </td>
                    <td className="text-right font-medium text-white">{item.totalHours}h</td>
                    <td className="text-right text-dark-400">
                      {totalHours > 0 ? Math.round((item.totalHours / totalHours) * 100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-dark-700">
                  <td className="font-medium text-white">Total</td>
                  <td className="text-right font-bold text-white">{totalHours}h</td>
                  <td className="text-right text-dark-400">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-dark-500">
            No data available
          </div>
        )}
      </div>
    </div>
  );
}
